export const DEFAULT_LEXIQUE_IMAGE = "/img/diapo/slider_judo_06.webp";

export const DEFAULT_LEXIQUE_FILTERS = {
  search: "",
  letter: "all",
  sort: "japonaisAsc",
};

const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
  numeric: true,
});

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function normalizeDiscipline(value) {
  const normalized = normalizeText(value);

  if (normalized.includes("karate")) return "Karate";

  return "Judo";
}

export function getLexiqueEntryKey(entry, index = "") {
  if (entry?.id) return `lexique-${entry.id}`;

  return `lexique-${entry?.japonais || "item"}-${entry?.traduction || ""}-${index}`;
}

export function getJapaneseTerm(entry) {
  return entry?.japonais || entry?.terme || entry?.nom || "";
}

export function getTranslation(entry) {
  return entry?.traduction || entry?.francais || entry?.definition || "";
}

export function getEntryFirstLetter(entry) {
  const japonais = normalizeText(getJapaneseTerm(entry));

  if (!japonais) return "#";

  const firstLetter = japonais.charAt(0).toUpperCase();

  if (!/^[A-Z]$/.test(firstLetter)) return "#";

  return firstLetter;
}

export function buildLetterOptions(entries = []) {
  const letters = Array.from(
    new Set(entries.map((entry) => getEntryFirstLetter(entry)).filter(Boolean)),
  ).sort((a, b) => collatorFR.compare(a, b));

  return ["all", ...letters];
}

export function sortLexiqueEntries(entries = [], sort = "japonaisAsc") {
  const sorted = [...entries];

  if (sort === "traductionAsc") {
    return sorted.sort((a, b) =>
      collatorFR.compare(getTranslation(a), getTranslation(b)),
    );
  }

  if (sort === "letterAsc") {
    return sorted.sort((a, b) => {
      const letterA = getEntryFirstLetter(a);
      const letterB = getEntryFirstLetter(b);

      if (letterA !== letterB) return collatorFR.compare(letterA, letterB);

      return collatorFR.compare(getJapaneseTerm(a), getJapaneseTerm(b));
    });
  }

  return sorted.sort((a, b) =>
    collatorFR.compare(getJapaneseTerm(a), getJapaneseTerm(b)),
  );
}

export function filterLexiqueEntries(
  entries = [],
  filters = DEFAULT_LEXIQUE_FILTERS,
) {
  const search = normalizeText(filters.search);

  const filtered = entries.filter((entry) => {
    const japonais = getJapaneseTerm(entry);
    const traduction = getTranslation(entry);
    const firstLetter = getEntryFirstLetter(entry);

    const searchableContent = normalizeText([japonais, traduction].join(" "));

    const matchesSearch = !search || searchableContent.includes(search);

    const matchesLetter =
      filters.letter === "all" || firstLetter === filters.letter;

    return matchesSearch && matchesLetter;
  });

  return sortLexiqueEntries(filtered, filters.sort);
}

export function getLexiqueGroups(entries = []) {
  const groups = entries.reduce((acc, entry, index) => {
    const letter = getEntryFirstLetter(entry);
    const key = letter || "#";

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push({
      ...entry,
      __key: getLexiqueEntryKey(entry, index),
    });

    return acc;
  }, {});

  return Object.entries(groups)
    .sort(([letterA], [letterB]) => collatorFR.compare(letterA, letterB))
    .map(([letter, items]) => ({
      letter,
      items,
    }));
}

export function countActiveLexiqueFilters(
  filters = DEFAULT_LEXIQUE_FILTERS,
) {
  return Object.entries(filters).reduce((count, [key, value]) => {
    if (key === "letter" && value === "all") return count;
    if (key === "sort" && value === "japonaisAsc") return count;
    if (!value) return count;

    return count + 1;
  }, 0);
}

export function getLexiqueStats(entries = []) {
  const letters = new Set(
    entries.map((entry) => getEntryFirstLetter(entry)).filter(Boolean),
  );

  const withTranslation = entries.filter((entry) =>
    Boolean(getTranslation(entry)),
  ).length;

  return {
    total: entries.length,
    letters: letters.size,
    withTranslation,
    withoutTranslation: Math.max(entries.length - withTranslation, 0),
  };
}